import { NotifyErrorWrapper, NotifyFail, NotifySuccess, NotifySuccessWrapper } from '@/helpers/core/notifications'
import {
  deactivateSiteMapRacks,
  deactivateSiteMapStructures,
  getHostingClients,
  getSiteMapMiners,
  getSiteMapRacks,
  getSiteMapStructures,
  reactivateSiteMapRacks,
  reactivateSiteMapStructures,
  syncSiteMapWithForeman,
} from '@/services/eos/site-map'
import type {
  IHostingClientFilters,
  ISiteMapMinerFilters,
  ISiteMapRackFilters,
  ISiteMapStructureFilters,
} from '@/types/eos/filters'
import type { AdminPortalAPIError } from '@/types/shared/errors'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

export const useSiteMap = () => {
  const queryClient = useQueryClient()

  const getStructuresQuery = (filters: ISiteMapStructureFilters) =>
    useQuery({
      queryKey: ['site-map-structures', filters],
      queryFn: () => getSiteMapStructures(filters),
    })

  const getRacksQuery = (filters: ISiteMapRackFilters) =>
    useQuery({
      queryKey: ['site-map-racks', filters],
      queryFn: () => getSiteMapRacks(filters),
    })

  const getMinersQuery = (filters: ISiteMapMinerFilters) =>
    useQuery({
      queryKey: ['site-map-miners', filters],
      queryFn: () => getSiteMapMiners(filters),
    })

  const getHostingClientsQuery = (filters: IHostingClientFilters) =>
    useQuery({
      queryKey: ['hosting-clients', filters],
      queryFn: () => getHostingClients(filters),
    })

  const syncSiteMapMutation = useMutation({
    mutationFn: () => syncSiteMapWithForeman(),
    onError: (e: AdminPortalAPIError) => {
      if (e.status === 429) {
        NotifyFail('You can only sync your site map once per hour')
      } else {
        NotifyFail(e.message)
      }
    },
    onSuccess: NotifySuccessWrapper(`Sync command sent.`),
  })

  const deactivateStructuresMutation = useMutation({
    mutationFn: (ids: string[]) => deactivateSiteMapStructures(ids),
    onSuccess: (structures) => {
      queryClient.invalidateQueries({ queryKey: ['site-map-structures'] })
      queryClient.invalidateQueries({ queryKey: ['site-map-racks'] })
      NotifySuccess(`${structures.length} ${structures.length === 1 ? 'structure' : 'structures'} deactivated`)
    },
    onError: NotifyErrorWrapper('Failed to deactivate structures'),
  })

  const deactivateRacksMutation = useMutation({
    mutationFn: (ids: string[]) => deactivateSiteMapRacks(ids),
    onSuccess: (racks) => {
      queryClient.invalidateQueries({ queryKey: ['site-map-racks'] })
      NotifySuccess(`${racks.length} ${racks.length === 1 ? 'rack' : 'racks'} deactivated`)
    },
    onError: NotifyErrorWrapper('Failed to deactivate racks'),
  })

  const reactivateStructuresMutation = useMutation({
    mutationFn: (ids: string[]) => reactivateSiteMapStructures(ids),
    onSuccess: (structures) => {
      queryClient.invalidateQueries({ queryKey: ['site-map-structures'] })
      NotifySuccess(`${structures.length} ${structures.length === 1 ? 'structure' : 'structures'} reactivated`)
    },
    onError: NotifyErrorWrapper('Failed to reactivate structures'),
  })

  const reactivateRacksMutation = useMutation({
    mutationFn: (ids: string[]) => reactivateSiteMapRacks(ids),
    onSuccess: (racks) => {
      queryClient.invalidateQueries({ queryKey: ['site-map-racks'] })
      NotifySuccess(`${racks.length} ${racks.length === 1 ? 'rack' : 'racks'} reactivated`)
    },
    onError: NotifyErrorWrapper('Failed to reactivate racks'),
  })

  return {
    getStructuresQuery,
    getRacksQuery,
    getMinersQuery,
    getHostingClientsQuery,
    syncSiteMapMutation,
    deactivateStructuresMutation,
    deactivateRacksMutation,
    reactivateStructuresMutation,
    reactivateRacksMutation,
  }
}
